// The five actions as the grid runs them (day-view.md §7): one call
// each, the result said in one line, warnings as toasts, errors with
// their reason — and after every write a fresh board. The rules are
// the use-cases'; this file only turns their answers into words.

import {useState} from "react";
import {ApiRequestError, act} from "../api/client";
import type {
  AddClientInput, AddClientWire, AddSessionInput, BoardCancelled, BoardCell,
  BoardClient, CancelledBy, Channel, UndoAddSessionInput,
} from "../api/contract";
import {
  CHANNEL_TR, ERROR_CODE_TR, HOLDER_TR, PHONE_OUTCOME_TR, WA_OUTCOME_TR, WEEKDAY_TR, trDay,
  weekdayOf,
} from "../labels";
import {useMoney} from "../ui/blind-context";
import {useToasts} from "../ui/toast-context";

// Where a chat is the right tool: a prefilled sentence, one click.
export function chatLink(sentence: string): string {
  return `https://claude.ai/new?q=${encodeURIComponent(sentence)}`;
}

export function errorText(e: unknown): string {
  if (e instanceof ApiRequestError) {
    const head = ERROR_CODE_TR[e.code] ?? e.code;
    return e.message && e.message !== head ? `${head}: ${e.message}` : head;
  }
  return e instanceof Error ? e.message : String(e);
}

export interface DuplicateAsk {
  suspects: {eventId: string; summary: string}[];
  retry: () => Promise<void>;
}

// What "Not düş" and "Danışan bilgisi" need of a child — a board
// client has it, and so does a cancelled slot's child from the drawer.
export type ClientRef = Pick<BoardClient, "clientId" | "name" | "deskNote">;

// What "Ödeme alındı" collects: the channel, and the amount when it is
// not the pair's fee.
export interface PayCollect {
  channel: Channel;
  amountTL?: number;
}

export function useActions(onDone: () => void, demo = false) {
  const toast = useToasts();
  // Gizli mod reaches the toasts: what was taken is said without the figure.
  const {tl, text} = useMoney();
  const [busy, setBusy] = useState<string | null>(null); // which action runs
  const [duplicate, setDuplicate] = useState<DuplicateAsk | null>(null);

  // One action at a time; a second tap while one runs is ignored,
  // which with the idempotency key is what makes a double tap one
  // record. In the dev-only demo nothing is sent.
  const run = async (key: string, work: () => Promise<void>) => {
    if (busy) return;
    setBusy(key);
    try {
      if (demo) {
        await new Promise((r) => setTimeout(r, 400));
        toast.ok(`DEMO — ${key} çalıştırılmadı`);
      } else {
        await work();
        onDone();
      }
    } catch (e) {
      toast.error(errorText(e));
      // The board went stale under the desk's hands: read it again.
      if (e instanceof ApiRequestError && e.code === "conflict") onDone();
    } finally {
      setBusy(null);
    }
  };

  const sayWarnings = (ws: string[] | undefined) => {
    for (const w of ws ?? []) toast.warn(text(w));
  };

  const uncancel = (cancellationId: string) =>
    run(`uncancel:${cancellationId}`, async () => {
      const {data} = await act.uncancelSession({cancellationId});
      toast.ok(`${data.time ?? ""} ${data.summary} geri alındı — takvimde yerinde.`);
    });

  const restore = (rec: BoardCancelled) => uncancel(rec.cancellationId);

  // Ödeme alındı — the pair's fee (D1) unless the sheet names another
  // amount: less leaves the rest open, more stays as credit — or
  // "seans yapıldı, ödeme yok" when collect is null.
  const pay = (cell: BoardCell, client: BoardClient, collect: PayCollect | null,
    confirmDuplicate = false): Promise<void> => run(`pay:${client.clientId}`, async () => {
    const body = {
      items: [{
        clientId: client.clientId,
        providerId: cell.providerId,
        date: cell.start.slice(0, 10),
        time: cell.time,
        durationMin: cell.durationMin,
        format: (cell.isGroup ? "group" : "individual") as "group" | "individual",
        calendarEventId: cell.id,
        ...(collect ? {collect: {
          channel: collect.channel,
          ...(collect.amountTL !== undefined ? {amountTL: collect.amountTL} : {}),
        }} : {}),
        idempotencyKey: `${cell.id}|${client.clientId}|${cell.start.slice(0, 10)}`,
      }],
      ...(confirmDuplicate ? {confirmDuplicate: true} : {}),
    };
    try {
      const {data, warnings} = await act.recordSession(body);
      const row = data.results[0];
      const paid = collect ?
        `${tl(collect.amountTL ?? row.feeTL)} ${channelWord(collect.channel)} alındı` :
        "seans kaydedildi";
      const {collectNowTL, creditTL} = row.position;
      const left = typeof collectNowTL === "number" && collectNowTL > 0 ?
        ` · kalan alınacak ${tl(collectNowTL)}` :
        typeof creditTL === "number" && creditTL > 0 ? ` · kredi ${tl(creditTL)}` : "";
      // A wrong tap is taken back from the toast the way an iptal is.
      toast.ok(`${row.clientName}: ${paid}${left}`, {
        label: "Geri al",
        run: () => undoRecord([row.serviceId], row.clientId),
      });
      sayWarnings(row.warnings);
      sayWarnings(warnings);
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "duplicate_suspect") {
        const suspects = ((e.detail as {suspects?: {eventId: string; summary: string}[]} | null)
          ?.suspects) ?? [];
        setDuplicate({
          suspects,
          retry: async () => {
            setDuplicate(null);
            await pay(cell, client, collect, true);
          },
        });
        return;
      }
      throw e;
    }
  });

  // "Geri al" on a recorded seans: back to not done, not paid. A
  // tahsilat that already carries a makbuz is refused by the door,
  // with the reason, and stays a chat job.
  const undoRecord = (serviceIds: string[], clientId: string) =>
    run(`undo:${clientId}`, async () => {
      for (const serviceId of serviceIds) {
        const {data, warnings} = await act.undoSessionRecord({serviceId});
        const back = data.returnedTL > 0 ? ` · ${tl(data.returnedTL)} tahsilat silindi` : "";
        toast.ok(`${data.clientName}: seans kaydı geri alındı — yapılmadı, ödenmedi${back}`);
        sayWarnings(data.warnings);
        sayWarnings(warnings);
      }
    });

  // Nakit ⇄ havale on the tahsilat taken with the seans. "bank" is the
  // wire's word for havale; the door books it iban_owner (Seda's IBAN).
  const changeChannel = (serviceId: string, clientId: string, channel: "cash" | "bank") =>
    run(`channel:${clientId}`, async () => {
      const {data, warnings} = await act.changePaymentChannel({serviceId, channel});
      toast.ok(`${data.clientName}: ${tl(data.amountTL)} ${CHANNEL_TR[data.from]} → ` +
        `${CHANNEL_TR[data.to]} olarak düzeltildi`);
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });

  const cancel = (cell: BoardCell, reason: string, cancelledBy: CancelledBy) =>
    run(`cancel:${cell.id}`, async () => {
      const {data, warnings} = await act.cancelSession({
        providerId: cell.providerId, date: cell.start.slice(0, 10),
        eventId: cell.id, reason, cancelledBy,
      });
      if (!data.cancelled) {
        toast.error(data.error);
        return;
      }
      const names = data.clientNames.join(", ") || data.summary;
      const id = data.cancellationId;
      toast.ok(`${data.time ?? ""} ${names} iptal edildi — ${reason}`, {
        label: "Geri al",
        run: () => uncancel(id),
      });
      sayWarnings(warnings);
    });

  // Same day, same column: the instance moves, the series does not
  // (1.6). The use-case warns about an overlap; it never blocks one.
  const move = (cell: BoardCell, time: string) =>
    run(`move:${cell.id}`, async () => {
      const {data, warnings} = await act.moveSession({
        providerId: cell.providerId, eventId: cell.id,
        date: cell.start.slice(0, 10), time,
      });
      if (!data.moved) {
        toast.warn(`${data.summary} zaten bu saatte — değişiklik yok.`);
        return;
      }
      toast.ok(`${data.summary}: ${data.from.start} → ${data.to.start} taşındı — ` +
        "danışmanın takviminde.");
      sayWarnings(warnings);
    });

  const note = (client: ClientRef, text: string, pin: boolean) =>
    run(`note:${client.clientId}`, async () => {
      const {data} = await act.addClientNote({
        clientId: client.clientId, text, category: "other", pin,
      });
      toast.ok(`${data.clientName}: not düşüldü${data.pinned ? " ve hücreye alındı" : ""}.`);
    });

  const whatsapp = (cell: BoardCell, client: BoardClient) =>
    run(`wa:${cell.id}:${client.clientId}`, async () => {
      const {data} = await act.sendSessionConfirmation({
        providerId: cell.providerId, eventId: cell.id, clientId: client.clientId,
      });
      if (!data.enabled) {
        toast.warn("WhatsApp onayları kapalı — gönderilmedi.");
        return;
      }
      for (const r of data.results) {
        const text = `${r.clientName}: ${WA_OUTCOME_TR[r.outcome] ?? r.outcome}` +
          (r.error ? ` (${r.error})` : "");
        if (r.outcome === "sent") toast.ok(text);
        else if (r.outcome === "failed") toast.error(text);
        else toast.warn(text);
      }
    });

  // The day's send (Onur, 22.10.2026): the ticked children one after
  // another, each through the same door as the drawer's button, so the
  // pilot, consent and claim-or-skip rules are unchanged. One line at
  // the end says what went; failures and skips are named.
  const whatsappMany = (items: {cell: BoardCell; client: BoardClient}[],
    onStep: (done: number) => void) =>
    run("wa:day", async () => {
      let sent = 0;
      const missed: string[] = [];
      for (const [i, {cell, client}] of items.entries()) {
        try {
          const {data} = await act.sendSessionConfirmation({
            providerId: cell.providerId, eventId: cell.id, clientId: client.clientId,
          });
          if (!data.enabled) {
            toast.warn("WhatsApp onayları kapalı — gönderilmedi.");
            return;
          }
          for (const r of data.results) {
            if (r.outcome === "sent") sent++;
            else missed.push(`${r.clientName}: ${WA_OUTCOME_TR[r.outcome] ?? r.outcome}`);
          }
        } catch (e) {
          missed.push(`${client.name}: ${errorText(e)}`);
        }
        onStep(i + 1);
      }
      if (sent > 0) toast.ok(`${sent} WhatsApp onayı gönderildi`);
      for (const m of missed) toast.warn(m);
    });

  // The desk called and the family is coming: the same ✔ and 📅✔️ a
  // WhatsApp "Onaylıyorum" gives. undo takes a phone confirmation back.
  const phoneConfirm = (cell: BoardCell, client: BoardClient, undo: boolean) =>
    run(`phone:${cell.id}:${client.clientId}`, async () => {
      const {data} = await act.confirmSessionByPhone({
        providerId: cell.providerId, eventId: cell.id, clientId: client.clientId,
        ...(undo ? {undo: true} : {}),
      });
      const text = `${data.clientName}: ${PHONE_OUTCOME_TR[data.outcome] ?? data.outcome}`;
      if (data.outcome === "confirmed" || data.outcome === "undone") toast.ok(text);
      else toast.warn(text);
    });

  // Ver in the Kasa drawer (Onur, 22.10.2026): a danışman's hakediş
  // from the kasa or Seda's account, never more than due, and Seda's
  // devir up to what the drawer can spare — the door says no to more.
  // One line says what went, with Geri al. Each resolves true once
  // recorded, so the sheet can step back; on a refusal it stays open.
  const payProvider = async (providerId: string, amountTL: number,
    from: "kasa" | "owner", idempotencyKey: string): Promise<boolean> => {
    let done = false;
    await run(`payout:${providerId}`, async () => {
      const {data, warnings} = await act.payProvider({providerId, amountTL, from, idempotencyKey});
      done = true;
      toast.ok(`${data.providerName}: ${tl(data.amountTL)} verildi · ${HOLDER_TR[data.from]}`, {
        label: "Geri al",
        run: () => undoPayout(data.paymentIds, data.providerName),
      });
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });
    return done;
  };

  const sedaDevir = async (amountTL: number, idempotencyKey: string): Promise<boolean> => {
    let done = false;
    await run("devir", async () => {
      const {data, warnings} = await act.sedaDevir({amountTL, idempotencyKey});
      done = true;
      const split = data.ownerDrawTL > 0 ?
        ` · hakediş ${tl(data.hakedisTL)}, çekiş ${tl(data.ownerDrawTL)}` : "";
      toast.ok(`${data.name}: ${tl(data.givenTL)} kasadan verildi${split}`, {
        label: "Geri al",
        run: () => undoPayout(data.paymentIds, data.name),
      });
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });
    return done;
  };

  // The toast's Geri al: today's payout events voided; an older one is
  // a chat job, and the door says so.
  const undoPayout = (paymentIds: string[], name: string) =>
    run(`undo-payout:${paymentIds[0] ?? ""}`, async () => {
      const {data, warnings} = await act.undoPayout({paymentIds});
      toast.ok(`${name}: ${tl(data.amountTL)} ödeme geri alındı`);
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });

  // Seans ekle (Onur, 03.11.2026): the seans on her calendar, said in
  // one line, with Geri al for the first half hour. true = done, the
  // sheet closes (the dev demo too, which sends nothing).
  const addSession = async (input: AddSessionInput, providerName: string): Promise<boolean> => {
    let done = false;
    await run(`add-session:${input.clientId}`, async () => {
      const {data, warnings} = await act.addSession(input);
      done = true;
      const day = WEEKDAY_TR[weekdayOf(data.date)].toLocaleLowerCase("tr-TR");
      const when = data.repeat === "once" ? `${trDay(data.date)} ${data.time}` :
        `${data.repeat === "weekly" ? "her" : "iki haftada bir"} ${day} ${data.time}, ` +
        `ilk seans ${trDay(data.date)}`;
      toast.ok(`${data.clientName}: ${when} — ${providerName}`, {
        label: "Geri al",
        run: () => undoAddSession(data.undo, data.clientName),
      });
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });
    return done || demo;
  };

  // Yeni danışan: the card with its veli and the pair's terms; null
  // when nothing was made (an error, said in its toast — or the demo).
  const addClient = async (input: AddClientInput): Promise<AddClientWire | null> => {
    let made: AddClientWire | null = null;
    await run("add-client", async () => {
      const {data, warnings} = await act.addClient(input);
      made = data;
      toast.ok(`${data.name}: kart açıldı — ${data.providerName}, ${tl(data.feeTL)}, ` +
        `Nefes payı %${data.rate}`);
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });
    return made;
  };

  // The toast's Geri al: the seans, its program, the fee this call put
  // on the card and the reactivation, all off again — within 30 minutes,
  // while nothing is recorded on it; the door says why when not.
  const undoAddSession = (undo: UndoAddSessionInput, name: string) =>
    run(`undo-add-session:${undo.eventId}`, async () => {
      const {data, warnings} = await act.undoAddSession(undo);
      toast.ok(`${name}: seans geri alındı` +
        `${data.scheduleRemoved ? " — program silindi" : ""}` +
        `${data.deactivated ? ", kart yeniden pasif" : ""}.`);
      sayWarnings(data.warnings);
      sayWarnings(warnings);
    });

  return {busy, duplicate, dismissDuplicate: () => setDuplicate(null),
    pay, undoRecord, changeChannel, cancel, uncancel, restore, move, note, whatsapp,
    whatsappMany, phoneConfirm, payProvider, sedaDevir, undoPayout, addSession, addClient,
    undoAddSession};
}

export function channelWord(channel: Channel): string {
  switch (channel) {
  case "cash": return "nakit";
  case "pos": return "POS ile";
  case "bank": return "havale ile";
  case "iban_owner": return "havale ile";
  case "iban_provider": return "IBAN (danışman) ile";
  }
}
